import { memo, useMemo, useState, useEffect } from 'react';
import { Handle, Position, type NodeProps, useReactFlow } from 'reactflow';
import { jwt_decode } from '../../../utils/jwt_decode';
import { VideoPlayer } from '../../Assets/LiveStreamModal';
import baseapi from '../../../services/baseapi';
import { X } from 'lucide-react';

const StreamNode = memo(({ id, data }: NodeProps) => {
    const { assetId, assetName, status, agentId, organizationId, clientId } = data;
    const { deleteElements } = useReactFlow();

    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        deleteElements({ nodes: [{ id }] });
    };

    const [fetchedAgentId, setFetchedAgentId] = useState<number | null>(null);
    const [fetchedOrgId, setFetchedOrgId] = useState<number | null>(null);
    const [loadingAsset, setLoadingAsset] = useState(false);

    useEffect(() => {
        if (!agentId) {
            setLoadingAsset(true);
            baseapi.get('/assets', {
                params: {
                    query: JSON.stringify({ id: assetId }),
                    limit: 1
                }
            }).then(res => {
                if (res.data.status && res.data.data?.length > 0) {
                    const asset = res.data.data[0];
                    setFetchedAgentId(asset.agent_id);
                    setFetchedOrgId(asset.organization_id);
                }
            }).catch(err => {
                console.error("Error fetching asset details in StreamNode", err);
            }).finally(() => {
                setLoadingAsset(false);
            });
        }
    }, [assetId, agentId]);

    const finalAgentId = agentId || fetchedAgentId;
    const finalOrgId = organizationId || fetchedOrgId;

    const cameraObj = useMemo(() => {
        const jwt = jwt_decode();
        const tenant_name = jwt?.tenant_id || jwt?.tenant_name;
        const org_id = jwt?.organization_id;

        const streamPath = tenant_name
            ? `${tenant_name}-${finalOrgId || org_id}-${clientId}-${finalAgentId || ''}_${assetId}`
            : `${finalOrgId || org_id}-${clientId}-${finalAgentId || ''}_${assetId}`;

        const obj: any = {
            id: assetId,
            name: assetName || 'Camera Stream',
            status: status?.toLowerCase() === 'active' || status?.toLowerCase() === 'online' ? 'online' : 'offline',
            url: `https://${window.location.hostname}/${streamPath}/`,
            client_id: String(clientId),
            agent_id: finalAgentId !== undefined && finalAgentId !== null && finalAgentId !== '' ? Number(finalAgentId) : 1
        };

        return obj;
    }, [assetId, assetName, status, clientId, finalAgentId, finalOrgId]);

    const isOnline = cameraObj.status === 'online';

    if (loadingAsset) {
        return (
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-3 shadow-2xl w-[320px] h-[220px] flex items-center justify-center">
                <div className="relative">
                    <div className="w-10 h-10 border-4 border-indigo-500/10 border-t-indigo-500 rounded-full animate-spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="bg-slate-900 border border-slate-700 rounded-2xl p-3 shadow-2xl w-[320px]">
            {/* Header */}
            <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-800">
                <span className="text-[11px] font-bold text-gray-200 uppercase tracking-wider truncate max-w-[150px]">
                     {cameraObj.name}
                </span>
                <div className="flex items-center gap-2">
                    <span className="text-[9px] text-gray-400 font-bold uppercase tracking-widest flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-rose-500'} animate-pulse`} />
                        {isOnline ? 'Live' : 'Offline'}
                    </span>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-rose-400 transition-colors p-0.5 rounded hover:bg-slate-800"
                        title="Close Stream"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {/* Video Player */}
            <div className="relative aspect-video w-full rounded-lg overflow-hidden bg-black border border-slate-800 shadow-inner">
                {isOnline ? (
                    <VideoPlayer
                        camera={cameraObj}
                        isSinglePlayer={true}
                    />
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 p-4">
                        <p className="text-[11px] font-black uppercase tracking-wider text-rose-400">Offline</p>
                    </div>
                )}
            </div>

            {/* Target Handle so we can connect an edge from the camera node */}
            <Handle
                type="target"
                position={Position.Left}
                id="left"
                isConnectable={false}
                className="!w-2.5 !h-2.5 !bg-orange-500 !border-2 !border-gray-900 !rounded-full transition-transform hover:scale-125"
                style={{ left: -5 }}
            />
        </div>
    );
});

StreamNode.displayName = 'StreamNode';
export default StreamNode;
